// src/pages/admin/QuickLinksManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL } from '../../services/api';

const QuickLinksManager = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null); // Add user state
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    link: 'https://',
    icon: 'fas fa-link',
    display_order: 0,
    is_active: true
  });

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchLinks();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      link: 'https://',
      icon: 'fas fa-link',
      display_order: 0,
      is_active: true
    });
  };

  const handleLogout = () => {
    // Clear user data from localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    
    // Redirect to login page
    window.location.href = '/admin/login';
  };

  const fetchLinks = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get token from localStorage for authorization
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/quick_links.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        // Process the data to ensure proper types for admin panel
        const processedLinks = response.data.data.map(link => ({
          ...link,
          id: parseInt(link.id),
          display_order: parseInt(link.display_order),
          is_active: link.is_active === "1" || link.is_active === 1,
          created_at: link.created_at
        })).sort((a, b) => {
          // First sort by display_order, then by created_at (newest first)
          if (a.display_order === b.display_order) {
            return new Date(b.created_at) - new Date(a.created_at);
          }
          return a.display_order - b.display_order;
        });
        
        setLinks(processedLinks);
      } else {
        setError('Failed to load quick links');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      
      const dataToSend = {
        title: formData.title,
        link: formData.link,
        icon: formData.icon,
        display_order: formData.display_order,
        is_active: formData.is_active ? 1 : 0
      };
      
      // Get token for authorization
      const token = localStorage.getItem('token');
      
      if (editingLink) {
        response = await axios.put(
          `${API_BASE_URL}/quick_links.php?id=${editingLink.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/quick_links.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingLink ? 'Quick link updated!' : 'Quick link added!');
        setShowModal(false);
        resetForm();
        setEditingLink(null);
        fetchLinks();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving quick link: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this quick link?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/quick_links.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Quick link deleted!');
          fetchLinks();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete quick link: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting quick link');
        console.error(err);
      }
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      link: link.link,
      icon: link.icon || 'fas fa-link',
      display_order: parseInt(link.display_order),
      is_active: link.is_active === true || link.is_active === "1" || link.is_active === 1
    });
    setShowModal(true);
  };

  const handleReorder = async (direction, link) => {
    try {
      const currentLinks = [...links];
      const currentIndex = currentLinks.findIndex(item => item.id === link.id);
      
      const token = localStorage.getItem('token');
      
      if (direction === 'up' && currentIndex > 0) {
        // Swap with previous link
        const prevLink = currentLinks[currentIndex - 1];
        
        // Update current link with previous link's order
        await axios.put(`${API_BASE_URL}/quick_links.php?id=${link.id}`, {
          ...link,
          display_order: prevLink.display_order,
          is_active: (link.is_active === true || link.is_active === "1" || link.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Update previous link with current link's order
        await axios.put(`${API_BASE_URL}/quick_links.php?id=${prevLink.id}`, {
          ...prevLink,
          display_order: link.display_order,
          is_active: (prevLink.is_active === true || prevLink.is_active === "1" || prevLink.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setSuccess('Order updated!');
        setTimeout(() => setSuccess(''), 3000);
        
      } else if (direction === 'down' && currentIndex < currentLinks.length - 1) {
        // Swap with next link
        const nextLink = currentLinks[currentIndex + 1];
        
        // Update current link with next link's order
        await axios.put(`${API_BASE_URL}/quick_links.php?id=${link.id}`, {
          ...link,
          display_order: nextLink.display_order,
          is_active: (link.is_active === true || link.is_active === "1" || link.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Update next link with current link's order
        await axios.put(`${API_BASE_URL}/quick_links.php?id=${nextLink.id}`, {
          ...nextLink,
          display_order: link.display_order,
          is_active: (nextLink.is_active === true || nextLink.is_active === "1" || nextLink.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setSuccess('Order updated!');
        setTimeout(() => setSuccess(''), 3000);
      }
      
      fetchLinks();
      
    } catch (err) {
      console.error('Error reordering:', err);
      setError('Failed to reorder: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleToggleActive = async (link) => {
    try {
      const currentActive = link.is_active === true || link.is_active === "1" || link.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        title: link.title,
        link: link.link,
        icon: link.icon || 'fas fa-link',
        display_order: parseInt(link.display_order),
        is_active: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/quick_links.php?id=${link.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchLinks();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLink(null);
    resetForm();
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        alert('Link copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading quick links...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Quick Links Manager</h2>
            <p className="text-muted">Manage your website quick links</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => {
              // Calculate next order number for new link
              const nextOrder = links.length > 0 
                ? Math.max(...links.map(link => parseInt(link.display_order))) + 1 
                : 1;
              setFormData(prev => ({ 
                ...prev, 
                title: '',
                link: 'https://',
                icon: 'fas fa-link',
                display_order: nextOrder,
                is_active: true
              }));
              setShowModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>Add Quick Link
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="50">Order</th>
                  <th>Title</th>
                  <th>Link</th>
                  <th>Icon</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th width="200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {links.map((link, index) => {
                  const serialNumber = index + 1;
                  
                  return (
                    <tr key={link.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleReorder('up', link)}
                            disabled={parseInt(link.display_order) <= 1}
                          >
                            <i className="fas fa-arrow-up"></i>
                          </Button>
                          <span className="mx-2">{serialNumber}</span>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleReorder('down', link)}
                            disabled={parseInt(link.display_order) >= links.length}
                          >
                            <i className="fas fa-arrow-down"></i>
                          </Button>
                        </div>
                      </td>
                      <td>
                        {link.icon && <i className={`${link.icon} me-2`} />}
                        <strong>{link.title}</strong>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <small 
                            className="text-truncate" 
                            style={{ maxWidth: '200px' }}
                            title={link.link}
                          >
                            {link.link}
                          </small>
                          <Button
                            size="sm"
                            variant="outline-info"
                            className="ms-2"
                            onClick={() => handleCopyToClipboard(link.link)}
                          >
                            <i className="fas fa-copy"></i>
                          </Button>
                        </div>
                      </td>
                      <td>
                        <code className="small">{link.icon}</code>
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          checked={link.is_active === true || link.is_active === "1" || link.is_active === 1}
                          onChange={() => handleToggleActive(link)}
                          label={(link.is_active === true || link.is_active === "1" || link.is_active === 1) ? 'Active' : 'Inactive'}
                          key={`active-${link.id}-${link.is_active}`}
                        />
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(link.created_at)}
                        </small>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleEdit(link)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(link.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            
            {links.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-link fa-3x mb-3"></i>
                <p>No quick links found. Add your first quick link!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Quick Link Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingLink ? 'Edit Quick Link' : 'Add Quick Link'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Google, Facebook, Twitter"
                    />
                    <Form.Text className="text-muted">
                      Display title for the quick link
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Order</Form.Label>
                    <Form.Control
                      type="number"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      min="1"
                    />
                    <Form.Text className="text-muted">
                      Lower numbers appear first
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Link URL *</Form.Label>
                <Form.Control
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  required
                  placeholder="https://example.com"
                />
                <Form.Text className="text-muted">
                  Full URL including http:// or https://
                </Form.Text>
              </Form.Group>
              
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Icon Class</Form.Label>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="text"
                        name="icon"
                        value={formData.icon}
                        onChange={handleInputChange}
                        placeholder="fas fa-link"
                      />
                      {formData.icon && (
                        <div className="ms-3">
                          <i className={`${formData.icon} fa-lg text-primary`}></i>
                        </div>
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      FontAwesome classes (e.g., fas fa-home, fab fa-facebook)
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="is_active"
                        label="Active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Inactive links won't be shown on the website
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Common Icons Suggestions */}
              <div className="mb-3">
                <Form.Label className="d-block">Common Icons:</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {[
                    { icon: 'fas fa-link', label: 'Link' },
                    { icon: 'fas fa-home', label: 'Home' },
                    { icon: 'fas fa-envelope', label: 'Email' },
                    { icon: 'fas fa-phone', label: 'Phone' },
                    { icon: 'fas fa-globe', label: 'Website' },
                    { icon: 'fab fa-facebook', label: 'Facebook' },
                    { icon: 'fab fa-twitter', label: 'Twitter' },
                    { icon: 'fab fa-instagram', label: 'Instagram' },
                    { icon: 'fab fa-linkedin', label: 'LinkedIn' },
                    { icon: 'fab fa-youtube', label: 'YouTube' },
                    { icon: 'fab fa-whatsapp', label: 'WhatsApp' },
                    { icon: 'fas fa-download', label: 'Download' },
                    { icon: 'fas fa-file-pdf', label: 'PDF' },
                    { icon: 'fas fa-external-link-alt', label: 'External' }
                  ].map((item, idx) => (
                    <Button
                      key={idx}
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setFormData({...formData, icon: item.icon})}
                      className="d-flex align-items-center"
                    >
                      <i className={`${item.icon} me-2`}></i>
                      {item.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {editingLink && (
                <div className="alert alert-info">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Created: {formatDate(editingLink.created_at)}
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingLink ? 'Update' : 'Add'} Link
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default QuickLinksManager;